import { InputWithSign } from '@/modules/common/components/input-with-sign';
import styles from './index.module.sass';
import classNames from 'classnames';
import { ResultCard } from './components/result-card';
import { ICalculatorBag, ITotalData } from '../../ts/rustmix-buyer.types';
import { WeightInputField } from './components/weight-field';
import { GlutenInputField } from './components/gluten-field';
import { IdkInputField } from './components/idk-field';
import { BugInputField } from './components/bug-field';
import { ProteinInputField } from './components/protein-field/idk-field';
import { CpInputField } from './components/cp-field';
import { NatureInputField } from './components/nature-field/idk-field';
import { HumidityInputField } from './components/humidity-input-field/idk-field';
import { CardHeader } from './components/card-header';
import { targetMoistureBagSpecs } from '../../hooks';
import { DiContainer } from '@/modules/di/di-container';
import { BagsService } from '@/modules/bags/services/bags.service';
import { useEffect, useState } from 'react';
const bagsService = DiContainer.get(BagsService);

export const CalculatorCard = ({
  color,
  variant,
  sum,
  title,
  profitSum,
  setValue,
  data,
  pricePerTon,
  renewTotalData,
  selectSourceFx,
  targetMoistureData,
  expertMode,
  selectedBagId,
  id,
  clearBag,
  totalData,
  className,
}: ICalculatorCard) => {
  const [indicatedClass, setIndicatedClass] = useState(0);
  const isValidTemperature = (temp) => !isNaN(Number(temp)) && temp !== '';
  const temperature = isValidTemperature(data.temperature) ? data.temperature : '23';

  useEffect(() => {
    (async () => {
      const res: any = await bagsService.getWheatClass({
        wheat_type: String(data.wheatType) !== '' ? String(data.wheatType) : '1',
        protein: data.protein,
        gluten: data.gluten,
        idk: data.idk,
        hfn: data.cp,
        density: data.nature,
        temperature: temperature,
        moisture: data.humidity,
        weed: '1',
      });
      setIndicatedClass(res.wheat_class);
    })();
  }, [data]);
  return (
    <div
      className={classNames(styles.root, className)}
      style={{
        borderColor:
          color === 'red' ? '#E73A42' : color === 'green' ? '#66B28A' : '#409BFF',
      }}
    >
      <CardHeader
        sum={+sum}
        pricePerTon={pricePerTon}
        title={title}
        profitSum={profitSum}
        classNumber={indicatedClass}
        color={color}
        selectSourceFx={selectSourceFx}
        targetMoistureProfit={targetMoistureData.priceGain}
        expertMode={expertMode}
        selectedBagId={selectedBagId}
        id={id}
        clearBag={clearBag}
        renewTotalData={renewTotalData}
        weight={+data.weight}
        targetMoistureData={targetMoistureData}
        totalData={totalData}
      />

      <div
        className={classNames(
          styles.content,
          variant === 'profit' && styles.profitContent,
        )}
      >
        {variant === 'profit' ? (
          <ResultCard
            data={data}
            targetMoistureData={targetMoistureData}
            expertMode={expertMode}
          />
        ) : (
          <>
            {expertMode ? (
              <div className={styles.fieldWithDetails}>
                <WeightInputField
                  data={data}
                  setValue={setValue}
                  renewTotalData={renewTotalData}
                  className={styles.child}
                />
                <span
                  className={styles.value}
                  style={{
                    color: +targetMoistureData.weightGain < 0 ? '#E73A42' : '#007E3C',
                  }}
                >
                  {+targetMoistureData.weightGain > 0
                    ? `+${targetMoistureData.weightGain}`
                    : targetMoistureData.weightGain}{' '}
                  т
                  <span className={styles.sublabel}>
                    {+targetMoistureData.weightGain > 0
                      ? 'прибавка веса'
                      : 'потеря веса'}
                  </span>
                </span>
              </div>
            ) : (
              <WeightInputField
                data={data}
                setValue={setValue}
                renewTotalData={renewTotalData}
              />
            )}
            {expertMode ? (
              <div className={styles.fieldWithDetails}>
                <HumidityInputField
                  data={data}
                  setValue={setValue}
                  renewTotalData={renewTotalData}
                  className={styles.child}
                />
                <span
                  className={styles.value}
                  style={{
                    color:
                      +targetMoistureData.moistureCoeff < 0 ? '#E73A42' : '#007E3C',
                  }}
                >
                  {targetMoistureData.moistureCoeff.toFixed(2)} %
                  <span className={styles.sublabel}>
                    {+targetMoistureData.moistureCoeff > 0
                      ? 'величина увлажнения'
                      : 'величина усушки'}
                  </span>
                </span>
              </div>
            ) : (
              <HumidityInputField
                data={data}
                setValue={setValue}
                renewTotalData={renewTotalData}
              />
            )}
            <GlutenInputField
              data={data}
              setValue={setValue}
              renewTotalData={renewTotalData}
              className={styles.child}
            />
            <IdkInputField
              data={data}
              setValue={setValue}
              renewTotalData={renewTotalData}
            />
            <BugInputField
              data={data}
              setValue={setValue}
              renewTotalData={renewTotalData}
            />
            <ProteinInputField
              data={data}
              setValue={setValue}
              renewTotalData={renewTotalData}
            />
            <CpInputField
              data={data}
              setValue={setValue}
              renewTotalData={renewTotalData}
            />
            <NatureInputField
              data={data}
              setValue={setValue}
              renewTotalData={renewTotalData}
            />
          </>
        )}
      </div>
    </div>
  );
};

interface ICalculatorCard {
  id?: number;
  title: string;
  sum: number | string;
  color: 'green' | 'blue' | 'red';
  variant?: 'component' | 'profit';
  profitSum?: number | string;
  setValue?: (val: any) => void;
  data: ICalculatorBag;
  pricePerTon: number;
  renewTotalData?: () => void;
  selectSourceFx?: () => void;
  targetMoistureData: targetMoistureBagSpecs;
  expertMode: boolean;
  selectedBagId?: string;
  clearBag?: () => void;
  totalData: ITotalData;
  className?: string;
}
